import { DateTime } from "luxon";
import { isSameDay } from "@/lib/dateHelpers";
import { formatEventRegistrations, getEventListEndsAt, getEventListStartsAt } from "@/pages/events/_utils";
import { isRegistrationClosed, isRegistrationOpen } from "@/pages/events/[eventSlug]/utils";
import type { Session } from "@/pages/events/types";
import type { DashboardEventNode } from "../queries";

export type DashboardEventDateSource = Pick<DashboardEventNode, "startsAt" | "endsAt" | "timezone" | "sessions">;

const DASHBOARD_EVENT_DATE_FORMAT = "ccc, LLL d";

export type DashboardEventDateParts = {
  primary: string;
  secondary?: string;
};

export type DashboardScheduleEvent = {
  id: string;
  slug: string;
  date: DashboardEventDateParts;
  title: string;
  registration?: string;
  draft: boolean;
  startsAt: string | null | undefined;
  timezone?: string | null;
};

export type DashboardPastEvent = {
  id: string;
  slug: string;
  date: DashboardEventDateParts;
  title: string;
  registration: string;
};

function formatDashboardEventDatePart(isoDate: string, timezone?: string | null): string | null {
  const parsedDate = timezone ? DateTime.fromISO(isoDate, { zone: timezone }) : DateTime.fromISO(isoDate);
  if (!parsedDate.isValid) {
    return null;
  }

  return parsedDate.toFormat(DASHBOARD_EVENT_DATE_FORMAT);
}

function formatDashboardEventEndDay(isoDate: string, timezone?: string | null): string | null {
  const parsedDate = timezone ? DateTime.fromISO(isoDate, { zone: timezone }) : DateTime.fromISO(isoDate);
  if (!parsedDate.isValid) {
    return null;
  }

  return parsedDate.toFormat("d");
}

function isSameMonth(startsAt: string, endsAt: string, timezone?: string | null): boolean {
  const start = timezone ? DateTime.fromISO(startsAt, { zone: timezone }) : DateTime.fromISO(startsAt);
  const end = timezone ? DateTime.fromISO(endsAt, { zone: timezone }) : DateTime.fromISO(endsAt);
  if (!start.isValid || !end.isValid) {
    return false;
  }

  return start.hasSame(end, "month");
}

function toEventListDateSource(event: DashboardEventDateSource) {
  const startsAt = event.startsAt ?? "";
  const endsAt = event.endsAt ?? startsAt;

  return {
    startsAt,
    endsAt,
    timezone: event.timezone,
    sessions: (event.sessions ?? null) as Session[] | null
  };
}

function getDashboardEventDateBounds(event: DashboardEventDateSource) {
  const startsAt = event.startsAt;
  if (!startsAt) {
    return null;
  }

  const dateSource = toEventListDateSource(event);

  return {
    startsAt: getEventListStartsAt(dateSource),
    endsAt: getEventListEndsAt(dateSource),
    timezone: event.timezone
  };
}

export function getDashboardEventDateParts(event: DashboardEventDateSource): DashboardEventDateParts {
  const bounds = getDashboardEventDateBounds(event);
  if (!bounds) {
    return { primary: "--" };
  }

  const { startsAt, endsAt, timezone } = bounds;
  const startLabel = formatDashboardEventDatePart(startsAt, timezone);
  if (!startLabel) {
    return { primary: "--" };
  }

  if (isSameDay(startsAt, endsAt, timezone ?? undefined)) {
    return { primary: startLabel };
  }

  if (isSameMonth(startsAt, endsAt, timezone ?? undefined)) {
    const endDay = formatDashboardEventEndDay(endsAt, timezone);
    if (!endDay) {
      return { primary: startLabel };
    }

    return { primary: `${startLabel}-${endDay}` };
  }

  const endLabel = formatDashboardEventDatePart(endsAt, timezone);
  if (!endLabel) {
    return { primary: startLabel };
  }

  return {
    primary: startLabel,
    secondary: `- ${endLabel}`
  };
}

export function formatDashboardEventDateLabel(event: DashboardEventDateSource): string {
  const { primary, secondary } = getDashboardEventDateParts(event);
  return secondary ? `${primary}\n${secondary}` : primary;
}

export function formatScheduleDayLabel(event: DashboardEventDateSource): string {
  return formatDashboardEventDateLabel(event);
}

export function formatScheduleMonthLabel(startsAt: string | null | undefined, timezone?: string | null): string {
  if (!startsAt) {
    return "UPCOMING";
  }

  const parsedDate = timezone ? DateTime.fromISO(startsAt, { zone: timezone }) : DateTime.fromISO(startsAt);
  if (!parsedDate.isValid) {
    return "UPCOMING";
  }

  return parsedDate.toFormat("LLLL").toUpperCase();
}

export function formatDashboardEventRegistrations(event: DashboardEventNode): string {
  return formatEventRegistrations({
    registrationCount: event.registrationCount,
    sessions: event.sessions
  });
}

export function shouldShowDashboardScheduleRegistrations(event: DashboardEventNode): boolean {
  if (isRegistrationOpen(event)) {
    return true;
  }

  return isRegistrationClosed(event);
}

export function toDashboardScheduleEvent(event: DashboardEventNode) {
  return {
    id: event.id,
    slug: event.slug,
    date: getDashboardEventDateParts(event),
    title: event.title,
    registration: shouldShowDashboardScheduleRegistrations(event)
      ? formatDashboardEventRegistrations(event)
      : undefined,
    draft: event.draft,
    startsAt: event.startsAt,
    timezone: event.timezone
  };
}

export function toDashboardPastEvent(event: DashboardEventNode) {
  return {
    id: event.id,
    slug: event.slug,
    date: getDashboardEventDateParts(event),
    title: event.title,
    registration: formatDashboardEventRegistrations(event)
  };
}

export function groupScheduleEventsByMonth(events: DashboardEventNode[]) {
  const grouped = new Map<string, ReturnType<typeof toDashboardScheduleEvent>[]>();

  for (const event of events) {
    const startsAt = event.startsAt;
    const month = startsAt
      ? formatScheduleMonthLabel(getEventListStartsAt(toEventListDateSource(event)), event.timezone)
      : "UPCOMING";
    const current = grouped.get(month) ?? [];
    current.push(toDashboardScheduleEvent(event));
    grouped.set(month, current);
  }

  return [...grouped.entries()].map(([month, monthEvents]) => ({
    month,
    events: monthEvents
  }));
}
